import { formatMagnitude } from '@dawn/core/utils';
import { CONNECTED_SERVICE_VALUES, type ConnectedService } from '@dawn/feature-config/widgets';
import { FilesCoveredBody, type BrandIconId } from '@dawn/ui';

import type { WidgetConfig } from '@/config/dashboard.types';
import { useSettingsQuery } from '@/hooks/queries/use-settings-query';

import { WidgetShell } from '../WidgetShell';

/**
 * Production-supported services. Persisted `tenant_settings.connectedServices`
 * is Zod-validated server-side, but we re-check at the render boundary to drop
 * any stale ids that might survive a deploy where the registry has been
 * narrowed but old rows haven't been migrated yet.
 */
const VALID_SERVICES = new Set<string>(CONNECTED_SERVICE_VALUES);

// Compile-time guarantee that every `ConnectedService` value has a matching
// `BrandIconId`. `packages/ui` cannot reference `shared/feature-config` (the
// three-tier hierarchy forbids `packages/` → `shared/`), so the assertion
// lives here, in the consumer that imports both unions.
type _AssertConnectedServiceHasBrandAsset = ConnectedService extends BrandIconId ? true : never;
const _assertConnectedServiceHasBrandAsset: _AssertConnectedServiceHasBrandAsset = true;
void _assertConnectedServiceHasBrandAsset;

/**
 * Inline mirror of the dashboard API's `FilesCoveredResponse`. Kept local to
 * honor the "no shared contract package" deferral (design §5 #11) — the three
 * dashboard widgets each declare their own response shape at the wrapper
 * boundary rather than importing from a cross-app contract.
 */
interface FileTypeBreakdownItem {
  id: string;
  label: string;
  count: number;
  isHighlighted?: boolean;
}
interface FilesCoveredResponseData {
  filesCovered: number;
  totalFiles: number;
  coveragePercent: number;
  filesCoveredBreakdown: FileTypeBreakdownItem[];
  totalFilesBreakdown: FileTypeBreakdownItem[];
}

function narrowFilesCoveredData(input: unknown): FilesCoveredResponseData | null {
  if (input === null || typeof input !== 'object') return null;
  const candidate = input as Record<string, unknown>;
  if (!Array.isArray(candidate.filesCoveredBreakdown)) return null;
  if (!Array.isArray(candidate.totalFilesBreakdown)) return null;
  return candidate as unknown as FilesCoveredResponseData;
}

export function FilesCoveredWidget({ config }: { config: WidgetConfig }) {
  const settings = useSettingsQuery();
  const connectedServices = (settings.data?.connectedServices ?? []).filter(
    (service): service is ConnectedService => VALID_SERVICES.has(service)
  );

  return (
    <WidgetShell
      config={config}
      renderData={(envelopeData) => {
        const data = narrowFilesCoveredData(envelopeData);
        if (!data) return null;
        return (
          <FilesCoveredBody
            coveragePercent={data.coveragePercent}
            filesCovered={data.filesCovered}
            totalFiles={data.totalFiles}
            filesCoveredBreakdown={data.filesCoveredBreakdown}
            totalFilesBreakdown={data.totalFilesBreakdown}
            connectedServices={connectedServices}
            coveredRowLabel="Files covered"
            totalRowLabel="Total files"
            coveredPopoverTitle="Files Covered"
            totalPopoverTitle="Total Files"
            popoverCategoryLabel="File Type"
            connectedServicesLabel="Connected Services"
            formatNumber={formatMagnitude}
          />
        );
      }}
    />
  );
}
